"use client";

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import AlertDialog from "./AlertDialog";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import Select from "react-select";
import OpportunitySharePointFilesTab from "./OpportunitySharePointFilesTab";
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
  MapPinIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOffice2Icon,
  XMarkIcon,
  ChevronDownIcon,
} from "@heroicons/react/20/solid";

const API = "https://erp-production-72da01c8e651.herokuapp.com/api";
// NOTE: restrict this key by HTTP referrer in Google Cloud, or move it to an env var.
const GOOGLE_PLACES_KEY = "AIzaSyBnTWkimXgryzzdI3KVPWNQwvtJSrm2pAg";

const LEAD_SOURCE_CATEGORIES = [
  "Public Tender Platform",
  "Direct Invite",
  "Referral",
  "Outbound Prospecting",
  "Repeat Client",
];

const DISQUALIFICATION_CATEGORIES = [
  "Outside Niche",
  "Wrong Geography",
  "Too Small",
  "Too Big",
  "Bad GC History",
  "Schedule Conflict",
  "Incomplete Documents",
  "Estimation Capacity Issue",
  "Reviewed Estimate Late",
];

const EMPTY_ADDRESS = {
  formattedAddress: "",
  street: "",
  city: "",
  state: "",
  postalCode: "",
  country: "",
  latitude: null,
  longitude: null,
  placeId: "",
};

const DEFAULT_DISQUALIFY_EMAIL = {
  sendEmail: true,
  to: "",
  subject: "Lead Disqualification Notification",
  body: "",
};

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#111827",
    borderColor: state.isFocused ? "#f97316" : "#374151",
    color: "#e5e7eb",
    minHeight: "2.5rem",
    borderRadius: "0.5rem",
    boxShadow: state.isFocused ? "0 0 0 1px #f97316" : "none",
    "&:hover": { borderColor: "#f97316" },
  }),
  input: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    "input:focus": { boxShadow: "none" },
  }),
  singleValue: (provided) => ({ ...provided, color: "#e5e7eb" }),
  placeholder: (provided) => ({ ...provided, color: "#6b7280" }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? "#374151" : "#1f2937",
    color: state.isSelected ? "#fb923c" : "#e5e7eb",
    "&:active": { backgroundColor: "#4b5563" },
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#1f2937",
    border: "1px solid #374151",
    borderRadius: "0.5rem",
    overflow: "hidden",
    zIndex: 30,
  }),
};

const inputClass =
  "block w-full rounded-lg bg-gray-900 border border-gray-700 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 focus:outline-none";

const btnPrimary =
  "inline-flex items-center justify-center gap-1.5 rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-500/50 transition-colors";

const btnSecondary =
  "inline-flex items-center justify-center gap-1.5 rounded-lg bg-gray-700 px-4 py-2 text-sm font-semibold text-gray-200 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500/50 transition-colors";

const btnSuccess =
  "inline-flex items-center justify-center gap-1.5 rounded-lg bg-green-500/10 px-4 py-2 text-sm font-semibold text-green-400 ring-1 ring-green-500/20 hover:bg-green-500/20 transition-colors";

const btnDanger =
  "inline-flex items-center justify-center gap-1.5 rounded-lg bg-red-500/10 px-4 py-2 text-sm font-semibold text-red-400 ring-1 ring-red-500/20 hover:bg-red-500/20 transition-colors";

const BADGE_TONES = {
  green: {
    wrap: "bg-green-500/10 text-green-400 ring-green-500/20",
    dot: "fill-green-400",
  },
  red: {
    wrap: "bg-red-500/10 text-red-400 ring-red-500/20",
    dot: "fill-red-400",
  },
  yellow: {
    wrap: "bg-yellow-500/10 text-yellow-400 ring-yellow-500/20",
    dot: "fill-yellow-400",
  },
  gray: {
    wrap: "bg-gray-500/10 text-gray-300 ring-gray-500/20",
    dot: "fill-gray-400",
  },
};

function Badge({ tone = "gray", children }) {
  const t = BADGE_TONES[tone];
  return (
    <span
      className={classNames(
        "inline-flex items-center gap-x-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset",
        t.wrap,
      )}
    >
      <svg
        viewBox="0 0 6 6"
        aria-hidden="true"
        className={classNames("size-1.5", t.dot)}
      >
        <circle r={3} cx={3} cy={3} />
      </svg>
      {children}
    </span>
  );
}

const leadStatusTone = (s) =>
  s === "qualified" ? "green" : s === "disqualified" ? "red" : "gray";

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "N/A");

function Section({ title, action, children, className = "" }) {
  return (
    <section
      className={classNames(
        "rounded-xl bg-gray-800 ring-1 ring-white/10",
        className,
      )}
    >
      {title && (
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-3">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-orange-400">
            {title}
          </h3>
          {action}
        </div>
      )}
      <div className="p-5">{children}</div>
    </section>
  );
}

function Label({ children }) {
  return (
    <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-gray-400">
      {children}
    </label>
  );
}

function FormField({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-400">
        {label}
      </dt>
      <dd className="mt-1 break-words text-sm text-gray-100">{children}</dd>
    </div>
  );
}

function Modal({ title, onClose, children, maxWidth = "max-w-lg" }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
      <div
        className={classNames(
          "w-full rounded-2xl bg-gray-800 shadow-2xl ring-1 ring-white/10",
          maxWidth,
        )}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
          <h3 className="text-lg font-semibold text-white">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-gray-400 hover:bg-gray-700 hover:text-gray-100"
            aria-label="Close"
          >
            <XMarkIcon className="size-5" />
          </button>
        </div>
        <div className="max-h-[calc(100vh-10rem)] overflow-y-auto p-5">
          {children}
        </div>
      </div>
    </div>
  );
}

const normalizeAddress = (a) => ({
  formattedAddress: a?.formattedAddress || "",
  street: a?.street || "",
  city: a?.city || "",
  state: a?.state || "",
  postalCode: a?.postalCode || "",
  country: a?.country || "",
  latitude: a?.latitude ?? null,
  longitude: a?.longitude ?? null,
  placeId: a?.placeId || "",
});

const leadToFormData = (l) => ({
  contactId: l.contactId?._id || l.contactId?.$oid || l.contactId || null,
  jobTitle: l.jobTitle || "",
  jobAddress: normalizeAddress(l.jobAddress),
  description: l.description || "",
  drawingLinks: l.drawingLinks && l.drawingLinks.length ? l.drawingLinks : [""],
  status: l.status || "unqualified",
  sourceCategory: l.sourceCategory || "",
  attachments: l.attachments || [],
});

export default function LeadDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSubTab, setActiveSubTab] = useState("General");
  const [isEditing, setIsEditing] = useState(false);
  const [isQualifying, setIsQualifying] = useState(false);
  const [isDisqualifying, setIsDisqualifying] = useState(false);
  const [formData, setFormData] = useState({
    contactId: null,
    jobTitle: "",
    jobAddress: { ...EMPTY_ADDRESS },
    description: "",
    drawingLinks: [""],
    status: "unqualified",
    sourceCategory: "",
    attachments: [],
  });
  const [qualifyFormData, setQualifyFormData] = useState({
    estimator: null,
    estimateDueDate: "",
    qualificationReason: "",
  });
  const [disqualifyFormData, setDisqualifyFormData] = useState({
    disqualificationReason: "",
    disqualificationCategory: "",
  });
  const [disqualifyEmailForm, setDisqualifyEmailForm] = useState({
    ...DEFAULT_DISQUALIFY_EMAIL,
  });
  const [alert, setAlert] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    onConfirm: () => {},
  });

  const closeAlert = () => setAlert((prev) => ({ ...prev, open: false }));
  const showAlert = (type, title, message, onConfirm = closeAlert) =>
    setAlert({ open: true, type, title, message, onConfirm });
  const showError = (message) => showAlert("error", "Error", message);

  const authConfig = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });

  useEffect(() => {
    const fetchLead = async () => {
      try {
        const [
          leadResponse,
          contactsResponse,
          accountsResponse,
          usersResponse,
        ] = await Promise.all([
          axios.get(`${API}/leads/${id}`, authConfig()),
          axios.get(`${API}/contacts`, authConfig()),
          axios.get(`${API}/accounts`, authConfig()),
          axios.get(`${API}/users`, authConfig()),
        ]);

        const leadData = leadResponse.data;
        setLead(leadData);
        setContacts(contactsResponse.data);
        setAccounts(accountsResponse.data);
        setUsers(
          usersResponse.data.filter(
            (user) => user.role === "project_manager" || user.role === "admin",
          ),
        );
        setFormData(leadToFormData(leadData));
        setLoading(false);
      } catch (err) {
        setError(
          "Failed to load lead: " +
            (err.response?.data?.message || err.message),
        );
        console.error("Fetch error:", err.response?.data || err);
        setLoading(false);
      }
    };
    fetchLead();
  }, [id]);

  const fetchDisqualificationEmailTemplate = async () => {
    try {
      const response = await axios.get(
        `${API}/company-profile/automated-leads/disqualification-email`,
        authConfig(),
      );
      return response.data.disqualificationEmailBody;
    } catch (err) {
      throw new Error(
        "Failed to fetch disqualification email template: " +
          (err.response?.data?.message || err.message),
      );
    }
  };

  const handleOpenDisqualifyModal = async () => {
    try {
      if (!lead || !lead.contactId) {
        return showError("Lead or contact information not found.");
      }

      const template = await fetchDisqualificationEmailTemplate();
      const emailBody = `Hello ${lead.contactId.firstName},\n\n${template}`;
      setDisqualifyEmailForm({
        sendEmail: true,
        to: lead.contactId.email,
        subject: lead.jobTitle,
        body: emailBody,
      });
      setDisqualifyFormData({
        disqualificationReason: "",
        disqualificationCategory: "",
      });
      setIsDisqualifying(true);
    } catch (err) {
      showError(err.message);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleContactChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      contactId: selectedOption ? selectedOption.value : null,
    }));
  };

  const handleDrawingLinkChange = (index, value) => {
    setFormData((prev) => {
      const drawingLinks = [...prev.drawingLinks];
      drawingLinks[index] = value;
      return { ...prev, drawingLinks };
    });
  };

  const addDrawingLink = () => {
    setFormData((prev) => ({
      ...prev,
      drawingLinks: [...prev.drawingLinks, ""],
    }));
  };

  const removeDrawingLink = (index) => {
    setFormData((prev) => ({
      ...prev,
      drawingLinks: prev.drawingLinks.filter((_, i) => i !== index),
    }));
  };

  const validateJobTitle = (jobTitle) => {
    const addressPattern =
      /\d+\s+[a-zA-Z\s]+(Ave|Avenue|St|Street|Rd|Road|Blvd|Boulevard)/i;
    return !addressPattern.test(jobTitle);
  };

  const handleAddressChange = async (place) => {
    if (!place || !place.value || !place.value.place_id) {
      return showError("Please select a valid address from the dropdown.");
    }

    const fallback = () =>
      setFormData((prev) => ({
        ...prev,
        jobAddress: {
          ...EMPTY_ADDRESS,
          formattedAddress: place.label || "",
          placeId: place.value.place_id || "",
        },
      }));

    try {
      const response = await axios.get(
        `${API}/leads/address/${place.value.place_id}`,
        authConfig(),
      );

      if (response.data.success && response.data.address) {
        const { address } = response.data;
        setFormData((prev) => ({
          ...prev,
          jobAddress: {
            ...normalizeAddress(address),
            placeId: address.placeId || place.value.place_id,
          },
        }));
      } else {
        showError(
          `Failed to fetch address details: ${
            response.data.message || "No address data returned"
          }`,
        );
        fallback();
      }
    } catch (err) {
      showError(
        `Error fetching address details: ${err.message}. Please try again or contact support.`,
      );
      fallback();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.contactId) return showError("Please select a contact.");
    if (!formData.jobTitle) return showError("Please enter a job title.");
    if (!validateJobTitle(formData.jobTitle)) {
      return showError(
        "Job title appears to be an address. Please enter a valid job title.",
      );
    }
    if (!formData.jobAddress.formattedAddress) {
      return showError("Please select a valid job address.");
    }

    try {
      const contact = contacts.find((c) => c._id === formData.contactId);
      if (!contact) return showError("Selected contact not found.");

      const companyId = contact.relatedAccount
        ? contact.relatedAccount.$oid || contact.relatedAccount
        : null;

      const formDataToSend = new FormData();
      formDataToSend.append("contactId", formData.contactId || "");
      formDataToSend.append("jobTitle", formData.jobTitle || "");
      formDataToSend.append("jobAddress", JSON.stringify(formData.jobAddress));
      formDataToSend.append("description", formData.description || "");
      formDataToSend.append(
        "drawingLinks",
        JSON.stringify(formData.drawingLinks.filter((l) => l.trim() !== "")),
      );
      formDataToSend.append("status", formData.status || "unqualified");
      formDataToSend.append("sourceCategory", formData.sourceCategory || "");
      formDataToSend.append("company", companyId || "");

      const response = await axios.put(`${API}/leads/${id}`, formDataToSend, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "multipart/form-data",
        },
        transformRequest: [(data) => data],
      });

      const updatedLead = response.data.lead || response.data;
      const mergedAddress = normalizeAddress(
        updatedLead.jobAddress?.formattedAddress
          ? updatedLead.jobAddress
          : formData.jobAddress,
      );

      setLead({ ...updatedLead, jobAddress: mergedAddress });
      setFormData(
        leadToFormData({ ...updatedLead, jobAddress: mergedAddress }),
      );
      setIsEditing(false);
      showAlert("success", "Success", "Lead updated successfully!");
    } catch (err) {
      console.error("Update lead error:", err.response?.data || err);
      showError(
        `Failed to update lead: ${err.response?.data?.message || err.message}`,
      );
    }
  };

  const handleQualifyInputChange = (e) => {
    const { name, value } = e.target;
    setQualifyFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDisqualifyInputChange = (e) => {
    const { name, value } = e.target;
    setDisqualifyFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDisqualifyEmailInputChange = (e) => {
    const { name, value } = e.target;
    setDisqualifyEmailForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEstimatorChange = (selectedOption) => {
    setQualifyFormData((prev) => ({
      ...prev,
      estimator: selectedOption ? selectedOption.value : null,
    }));
  };

  const handleQualify = async (e) => {
    e.preventDefault();
    if (!qualifyFormData.estimator)
      return showError("Please select an estimator.");
    if (!qualifyFormData.estimateDueDate) {
      return showError("Please select an estimate due date.");
    }
    if (!qualifyFormData.qualificationReason) {
      return showError("Please provide a qualification reason.");
    }

    try {
      const response = await axios.post(
        `${API}/leads/qualify/${id}`,
        qualifyFormData,
        authConfig(),
      );

      setIsQualifying(false);
      showAlert("success", "Success", "Lead qualified as opportunity!", () => {
        closeAlert();
        navigate(`/Opportunities/${response.data.opportunity._id}`, {
          state: { newOpportunity: response.data.opportunity },
        });
      });
    } catch (err) {
      showError(
        "Failed to qualify lead: " +
          (err.response?.data?.message || err.message),
      );
    }
  };

  const handleDisqualify = async (e) => {
    e.preventDefault();
    if (!disqualifyFormData.disqualificationReason) {
      return showError("Please provide a disqualification reason.");
    }

    try {
      const response = await axios.post(
        `${API}/leads/disqualify/${id}`,
        {
          disqualificationReason: disqualifyFormData.disqualificationReason,
          disqualificationCategory: disqualifyFormData.disqualificationCategory,
          sendEmail: disqualifyEmailForm.sendEmail,
          emailSubject: disqualifyEmailForm.sendEmail
            ? disqualifyEmailForm.subject
            : undefined,
          emailBody: disqualifyEmailForm.sendEmail
            ? disqualifyEmailForm.body
            : undefined,
        },
        authConfig(),
      );

      setLead(response.data.lead);
      setIsDisqualifying(false);
      setDisqualifyEmailForm({ ...DEFAULT_DISQUALIFY_EMAIL });
      setDisqualifyFormData({
        disqualificationReason: "",
        disqualificationCategory: "",
      });
      showAlert("success", "Success", "Lead disqualified successfully!");
    } catch (err) {
      showError(
        "Failed to disqualify lead: " +
          (err.response?.data?.message || err.message),
      );
    }
  };

  const closeDisqualifyModal = () => {
    setIsDisqualifying(false);
    setDisqualifyEmailForm({ ...DEFAULT_DISQUALIFY_EMAIL });
    setDisqualifyFormData({
      disqualificationReason: "",
      disqualificationCategory: "",
    });
  };

  const accountNameForContact = (contact) => {
    if (!contact || !contact.relatedAccount) return "None";
    const accountId = contact.relatedAccount.$oid || contact.relatedAccount;
    const account = accounts.find((a) => a._id === accountId);
    return account ? account.companyName : "None";
  };

  const getContactDetails = () => {
    if (!lead) {
      return { fullName: "Unknown Contact", email: "N/A", phoneNumber: "N/A" };
    }
    const contactId =
      lead.contactId?._id || lead.contactId?.$oid || lead.contactId;
    const contact = contacts.find((c) => c._id === contactId);
    return {
      fullName: contact
        ? `${contact.firstName} ${contact.lastName}`
        : "Unknown Contact",
      email: contact?.email || "N/A",
      phoneNumber: contact?.phoneNumber || "N/A",
      accountName: accountNameForContact(contact),
    };
  };

  const contactOptions = contacts.map((contact) => ({
    value: contact._id,
    label: `${contact.firstName} ${contact.lastName}`,
  }));

  const estimatorOptions = users.map((user) => ({
    value: user._id,
    label: user.displayName || user.email,
  }));

  const tabs = [
    { name: "General", current: activeSubTab === "General" },
    { name: "Files", current: activeSubTab === "Files" },
  ];

  if (loading) {
    return (
      <div className="py-16 text-center text-sm text-gray-400">
        Loading lead...
      </div>
    );
  }
  if (error) {
    return (
      <div className="mx-auto max-w-xl py-16">
        <div className="rounded-xl bg-red-500/10 p-4 text-center text-sm text-red-300 ring-1 ring-red-500/20">
          {error}
        </div>
      </div>
    );
  }

  const contactDetails = getContactDetails();
  const initials = contactDetails.fullName
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const selectedFormContact = contacts.find(
    (c) => c._id === formData.contactId,
  );

  return (
    <div className=" px-4 py-6">
      <div className="rounded-2xl bg-gray-900 p-4 shadow-xl ring-1 ring-white/10 sm:p-6">
        {/* Header */}
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-orange-400">
              Lead
            </p>
            <h1 className="mt-1 truncate text-2xl font-bold text-white">
              {lead.jobTitle}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Badge tone={leadStatusTone(lead.status)}>
                {capitalize(lead.status)}
              </Badge>
              {lead.sourceCategory && (
                <span className="rounded-full bg-gray-800 px-2.5 py-1 text-xs font-medium text-gray-300 ring-1 ring-inset ring-white/10">
                  {lead.sourceCategory}
                </span>
              )}
              {lead.disqualificationCategory && (
                <span className="rounded-full bg-red-500/10 px-2.5 py-1 text-xs font-medium text-red-300 ring-1 ring-inset ring-red-500/20">
                  {lead.disqualificationCategory}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => navigate("/leads")} className={btnSecondary}>
              <ArrowLeftIcon className="size-4" />
              Back to Leads
            </button>
            {lead.status !== "qualified" && (
              <button
                onClick={() => {
                  setQualifyFormData({
                    estimator: null,
                    estimateDueDate: "",
                    qualificationReason: "",
                  });
                  setIsQualifying(true);
                }}
                className={btnSuccess}
              >
                <CheckCircleIcon className="size-4" />
                Qualify
              </button>
            )}
            {lead.status !== "disqualified" && (
              <button onClick={handleOpenDisqualifyModal} className={btnDanger}>
                <XCircleIcon className="size-4" />
                Disqualify
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="grid grid-cols-1 sm:hidden">
            <select
              value={activeSubTab}
              onChange={(e) => setActiveSubTab(e.target.value)}
              aria-label="Select a tab"
              className="col-start-1 row-start-1 w-full appearance-none rounded-lg bg-gray-800 py-2 pr-8 pl-3 text-base font-semibold text-gray-200 outline-1 -outline-offset-1 outline-gray-700 focus:outline-2 focus:-outline-offset-2 focus:outline-orange-400"
            >
              {tabs.map((tab) => (
                <option key={tab.name} value={tab.name}>
                  {tab.name}
                </option>
              ))}
            </select>
            <ChevronDownIcon
              aria-hidden="true"
              className="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end fill-gray-400"
            />
          </div>
          <div className="hidden sm:block">
            <nav
              aria-label="Tabs"
              className="flex gap-1 rounded-xl bg-gray-800 p-1 ring-1 ring-white/10"
            >
              {tabs.map((tab) => (
                <button
                  key={tab.name}
                  onClick={() => setActiveSubTab(tab.name)}
                  aria-current={tab.current ? "page" : undefined}
                  className={classNames(
                    tab.current
                      ? "bg-orange-500 text-white shadow"
                      : "text-gray-400 hover:bg-gray-700 hover:text-gray-100",
                    "flex-1 rounded-lg px-4 py-2 text-center text-sm font-semibold transition-colors",
                  )}
                >
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* GENERAL */}
        {activeSubTab === "General" && (
          <div>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">
                {isEditing ? "Edit Lead" : "Lead Details"}
              </h2>
              {!isEditing && (
                <button
                  onClick={() => setIsEditing(true)}
                  className={btnPrimary}
                >
                  <PencilSquareIcon className="size-4" />
                  Edit
                </button>
              )}
            </div>

            {isEditing ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                <Section title="Contact">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <FormField label="Contact">
                      <Select
                        options={contactOptions}
                        value={
                          contactOptions.find(
                            (option) => option.value === formData.contactId,
                          ) || null
                        }
                        onChange={handleContactChange}
                        placeholder="Select a contact..."
                        isClearable
                        styles={customSelectStyles}
                      />
                    </FormField>
                    <FormField label="Related Account">
                      <p className="py-2 text-sm text-gray-200">
                        {accountNameForContact(selectedFormContact)}
                      </p>
                    </FormField>
                  </div>
                </Section>

                <Section title="Job">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <FormField label="Job Title">
                      <input
                        type="text"
                        name="jobTitle"
                        value={formData.jobTitle}
                        onChange={handleInputChange}
                        className={inputClass}
                        required
                        placeholder="Enter job title"
                      />
                    </FormField>
                    <FormField label="Lead Source">
                      <select
                        value={formData.sourceCategory}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            sourceCategory: e.target.value,
                          }))
                        }
                        className={inputClass}
                      >
                        <option value="">Select a source...</option>
                        {LEAD_SOURCE_CATEGORIES.map((cat) => (
                          <option key={cat} value={cat}>
                            {cat}
                          </option>
                        ))}
                      </select>
                    </FormField>
                    <FormField label="Status">
                      <select
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        className={inputClass}
                      >
                        <option value="unqualified">Unqualified</option>
                        <option value="qualified">Qualified</option>
                        <option value="disqualified">Disqualified</option>
                      </select>
                    </FormField>
                    <FormField label="Description" className="md:col-span-2">
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        rows={4}
                        className={inputClass}
                      />
                    </FormField>
                  </div>
                </Section>

                <Section title="Location">
                  <Label>Job Address</Label>
                  <GooglePlacesAutocomplete
                    autocompletionRequest={{
                      componentRestrictions: { country: ["ca"] },
                    }}
                    apiKey={GOOGLE_PLACES_KEY}
                    selectProps={{
                      onChange: handleAddressChange,
                      placeholder:
                        "Enter a job address (e.g., 123 Main St, Toronto, ON)",
                      value: formData.jobAddress.formattedAddress
                        ? {
                            label: formData.jobAddress.formattedAddress,
                            value: {
                              description: formData.jobAddress.formattedAddress,
                              place_id: formData.jobAddress.placeId || "",
                            },
                          }
                        : null,
                      styles: customSelectStyles,
                    }}
                  />
                </Section>

                <Section title="Drawing Links">
                  <div className="space-y-2">
                    {formData.drawingLinks.map((link, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input
                          type="url"
                          value={link}
                          onChange={(e) =>
                            handleDrawingLinkChange(index, e.target.value)
                          }
                          className={inputClass}
                          placeholder="https://example.com"
                        />
                        {formData.drawingLinks.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeDrawingLink(index)}
                            className="rounded-lg p-2 text-red-400 hover:bg-red-500/10"
                            aria-label="Remove link"
                          >
                            <TrashIcon className="size-5" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addDrawingLink}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-gray-700 px-3 py-1.5 text-sm font-medium text-orange-400 hover:bg-gray-600"
                    >
                      <PlusIcon className="size-4" />
                      Add Drawing Link
                    </button>
                  </div>
                </Section>

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setFormData(leadToFormData(lead));
                      setIsEditing(false);
                    }}
                    className={btnSecondary}
                  >
                    Cancel
                  </button>
                  <button type="submit" className={btnPrimary}>
                    Update Lead
                  </button>
                </div>
              </form>
            ) : (
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                <div className="space-y-6 lg:col-span-2">
                  <Section title="Job">
                    <dl className="grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-2">
                      <Field label="Job Title">{lead.jobTitle}</Field>
                      <Field label="Lead Source">
                        {lead.sourceCategory || "—"}
                      </Field>
                      <Field label="Description" className="md:col-span-2">
                        <span className="whitespace-pre-wrap">
                          {lead.description || "N/A"}
                        </span>
                      </Field>
                    </dl>
                  </Section>

                  <Section title="Location">
                    <div className="flex items-start gap-2 text-sm text-gray-100">
                      <MapPinIcon className="mt-0.5 size-5 shrink-0 text-orange-400" />
                      <span>{lead.jobAddress?.formattedAddress || "N/A"}</span>
                    </div>
                  </Section>

                  <Section title="Drawing Links">
                    {lead.drawingLinks && lead.drawingLinks.length ? (
                      <ul className="space-y-1.5">
                        {lead.drawingLinks.map((link, idx) => (
                          <li key={idx} className="truncate text-sm">
                            <a
                              href={link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-orange-400 hover:text-orange-300 hover:underline"
                            >
                              {link}
                            </a>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-gray-400">No drawing links</p>
                    )}
                  </Section>

                  {(lead.disqualificationCategory ||
                    lead.disqualificationReason) && (
                    <Section title="Disqualification">
                      <dl className="grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-2">
                        {lead.disqualificationCategory && (
                          <Field label="Category">
                            <Badge tone="red">
                              {lead.disqualificationCategory}
                            </Badge>
                          </Field>
                        )}
                        {lead.disqualificationReason && (
                          <Field label="Reason" className="md:col-span-2">
                            <span className="whitespace-pre-wrap">
                              {lead.disqualificationReason}
                            </span>
                          </Field>
                        )}
                      </dl>
                    </Section>
                  )}
                </div>

                <div className="lg:col-span-1">
                  <div className="lg:sticky lg:top-4">
                    <Section title="Contact">
                      <div className="mb-5 flex items-center gap-3">
                        <span className="flex size-12 shrink-0 items-center justify-center rounded-full bg-orange-500/10 text-base font-semibold text-orange-400 ring-1 ring-orange-500/20">
                          {initials || "?"}
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-gray-100">
                            {contactDetails.fullName}
                          </p>
                          <p className="truncate text-xs text-gray-400">
                            Lead contact
                          </p>
                        </div>
                      </div>
                      <dl className="space-y-4">
                        <div className="flex items-start gap-2.5">
                          <BuildingOffice2Icon className="mt-0.5 size-4 shrink-0 text-gray-500" />
                          <span className="text-sm text-gray-100">
                            {contactDetails.accountName}
                          </span>
                        </div>
                        <div className="flex items-start gap-2.5">
                          <EnvelopeIcon className="mt-0.5 size-4 shrink-0 text-gray-500" />
                          {contactDetails.email !== "N/A" ? (
                            <a
                              href={`mailto:${contactDetails.email}`}
                              className="break-all text-sm text-orange-400 hover:underline"
                            >
                              {contactDetails.email}
                            </a>
                          ) : (
                            <span className="text-sm text-gray-400">N/A</span>
                          )}
                        </div>
                        <div className="flex items-start gap-2.5">
                          <PhoneIcon className="mt-0.5 size-4 shrink-0 text-gray-500" />
                          <span className="text-sm text-gray-100">
                            {contactDetails.phoneNumber}
                          </span>
                        </div>
                      </dl>
                    </Section>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* FILES */}
        {activeSubTab === "Files" && (
          <OpportunitySharePointFilesTab
            entityId={id}
            entityName={lead.jobTitle}
            entityType="Lead"
            sharePointFolderName={lead.sharePointFolderName}
            setEntity={setLead}
          />
        )}

        {/* QUALIFY MODAL */}
        {isQualifying && (
          <Modal title="Qualify Lead" onClose={() => setIsQualifying(false)}>
            <form onSubmit={handleQualify} className="space-y-4">
              <FormField label="Estimator (Admin / PM)">
                <Select
                  options={estimatorOptions}
                  value={
                    estimatorOptions.find(
                      (option) => option.value === qualifyFormData.estimator,
                    ) || null
                  }
                  onChange={handleEstimatorChange}
                  placeholder="Select an estimator..."
                  isClearable
                  styles={customSelectStyles}
                />
              </FormField>
              <FormField label="Estimate Due Date">
                <input
                  type="date"
                  required
                  name="estimateDueDate"
                  value={qualifyFormData.estimateDueDate}
                  onChange={handleQualifyInputChange}
                  className={inputClass}
                />
              </FormField>
              <FormField label="Qualification Reason">
                <textarea
                  name="qualificationReason"
                  value={qualifyFormData.qualificationReason}
                  onChange={handleQualifyInputChange}
                  className={inputClass}
                  rows={4}
                  required
                  placeholder="Enter the reason for qualifying this lead..."
                />
              </FormField>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsQualifying(false)}
                  className={btnSecondary}
                >
                  Cancel
                </button>
                <button type="submit" className={btnPrimary}>
                  Qualify
                </button>
              </div>
            </form>
          </Modal>
        )}

        {/* DISQUALIFY MODAL */}
        {isDisqualifying && (
          <Modal title="Disqualify Lead" onClose={closeDisqualifyModal}>
            <form onSubmit={handleDisqualify} className="space-y-4">
              <FormField label="Disqualification Category">
                <select
                  name="disqualificationCategory"
                  value={disqualifyFormData.disqualificationCategory}
                  onChange={handleDisqualifyInputChange}
                  required
                  className={inputClass}
                >
                  <option value="">Select a category...</option>
                  {DISQUALIFICATION_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label="Disqualification Reason">
                <textarea
                  name="disqualificationReason"
                  value={disqualifyFormData.disqualificationReason}
                  onChange={handleDisqualifyInputChange}
                  className={inputClass}
                  rows={4}
                  required
                  placeholder="Enter the reason for disqualifying this lead..."
                />
              </FormField>

              <label className="flex items-center gap-2 rounded-lg bg-gray-900 px-3 py-2.5 text-sm font-medium text-gray-200 ring-1 ring-white/10">
                <input
                  type="checkbox"
                  checked={disqualifyEmailForm.sendEmail}
                  onChange={(e) =>
                    setDisqualifyEmailForm((prev) => ({
                      ...prev,
                      sendEmail: e.target.checked,
                    }))
                  }
                  className="size-4 rounded border-gray-600 bg-gray-700 text-orange-500 focus:ring-orange-500"
                />
                Send disqualification email
              </label>

              {disqualifyEmailForm.sendEmail && (
                <div className="space-y-4 rounded-lg bg-gray-900/60 p-4 ring-1 ring-white/10">
                  <FormField label="To">
                    <input
                      type="email"
                      name="to"
                      value={disqualifyEmailForm.to}
                      disabled
                      className={classNames(
                        inputClass,
                        "cursor-not-allowed opacity-60",
                      )}
                    />
                  </FormField>
                  <FormField label="Subject">
                    <input
                      type="text"
                      name="subject"
                      value={disqualifyEmailForm.subject}
                      onChange={handleDisqualifyEmailInputChange}
                      className={inputClass}
                    />
                  </FormField>
                  <FormField label="Email Body">
                    <textarea
                      name="body"
                      value={disqualifyEmailForm.body}
                      onChange={handleDisqualifyEmailInputChange}
                      className={inputClass}
                      rows={6}
                    />
                  </FormField>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeDisqualifyModal}
                  className={btnSecondary}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-red-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-400 focus:outline-none focus:ring-2 focus:ring-red-500/50 transition-colors"
                >
                  Disqualify
                </button>
              </div>
            </form>
          </Modal>
        )}

        <AlertDialog
          open={alert.open}
          type={alert.type}
          title={alert.title}
          message={alert.message}
          onClose={closeAlert}
          onConfirm={alert.onConfirm}
        />
      </div>
    </div>
  );
}
